// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  IlliquidAssetEventType,
  type AcquisitionEventCreateBody,
  type CapitalCallEventCreateBody,
  type CreateIlliquidAssetEventRequestBody,
  type DistributionEventCreateBody,
  type ValuationEventCreateBody,
} from '@shared/data-access/types/engine-types';
import type {
  IlliquidAcquisitionEventFormData,
  IlliquidCapitalCallEventFormData,
  IlliquidDistributionEventFormData,
  IlliquidEventBaseFormData,
  IlliquidEventFormData,
  IlliquidValuationEventFormData,
} from '../types/illiquid-event-form';

const mapEventBaseFields = (form: IlliquidEventBaseFormData) => ({
  event_date: form.eventDate,

  amount: form.amount,

  currency: form.currency,

  description: form.description ?? null,
});

export const buildAcquisitionEventCreateBody = (
  form: IlliquidAcquisitionEventFormData,
): AcquisitionEventCreateBody => ({
  event_type: IlliquidAssetEventType.Acquisition,

  subtype: null,

  ...mapEventBaseFields(form),
});

export const buildCapitalCallEventCreateBody = (
  form: IlliquidCapitalCallEventFormData,
): CapitalCallEventCreateBody => ({
  event_type: IlliquidAssetEventType.CapitalCall,

  subtype: form.subtype,

  ...mapEventBaseFields(form),
});

export const buildDistributionEventCreateBody = (
  form: IlliquidDistributionEventFormData,
): DistributionEventCreateBody => ({
  event_type: IlliquidAssetEventType.Distribution,

  subtype: form.subtype,

  ...mapEventBaseFields(form),
});

export const buildValuationEventCreateBody = (
  form: IlliquidValuationEventFormData,
): ValuationEventCreateBody => ({
  event_type: IlliquidAssetEventType.Valuation,

  subtype: null,

  ...mapEventBaseFields(form),
});

export const buildIlliquidEventCreateBody = (
  form: IlliquidEventFormData,
): CreateIlliquidAssetEventRequestBody => {
  switch (form.eventType) {
    case IlliquidAssetEventType.Acquisition:
      return buildAcquisitionEventCreateBody(form);

    case IlliquidAssetEventType.CapitalCall:
      return buildCapitalCallEventCreateBody(form);

    case IlliquidAssetEventType.Distribution:
      return buildDistributionEventCreateBody(form);

    case IlliquidAssetEventType.Valuation:
      return buildValuationEventCreateBody(form);
  }
};
